import { XMarkIcon } from "@heroicons/react/20/solid";

interface FormulaHelpModalProps {
  onClose: () => void;
}

const SECTIONS = [
  {
    title: "Field References",
    content: (
      <div className="space-y-2 text-sm text-gray-700">
        <p>
          Reference any field on the card using <code className="bg-gray-100 px-1 rounded font-mono">{`\${Field Name}`}</code>. The name is case-insensitive.
        </p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="bg-gray-50 rounded p-2 font-mono text-xs">{`\${Price}`}</div>
          <div className="text-xs text-gray-500 self-center">Value of the "Price" field</div>
          <div className="bg-gray-50 rounded p-2 font-mono text-xs">{`\${deal value}`}</div>
          <div className="text-xs text-gray-500 self-center">Case-insensitive match</div>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Start typing <code className="bg-gray-100 px-1 rounded font-mono">{"${"}</code> in the formula box to get autocomplete suggestions for field names.
        </p>
      </div>
    ),
  },
  {
    title: "Arithmetic Operators",
    content: (
      <div className="space-y-2 text-sm text-gray-700">
        <p>All standard math operators are supported between field references and numbers:</p>
        <table className="w-full text-xs border-collapse mt-1">
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-2 py-1 text-left font-medium">Operator</th>
              <th className="border px-2 py-1 text-left font-medium">Meaning</th>
              <th className="border px-2 py-1 text-left font-medium">Example</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["+", "Addition", "${Price} + ${Tax}"],
              ["-", "Subtraction", "${Revenue} - ${Cost}"],
              ["*", "Multiplication", "${Price} * ${Quantity}"],
              ["/", "Division", "${Total} / ${Count}"],
              ["%", "Modulo (remainder)", "${Value} % 100"],
              ["**", "Exponentiation", "${Base} ** 2"],
            ].map(([op, meaning, ex]) => (
              <tr key={op}>
                <td className="border px-2 py-1 font-mono">{op}</td>
                <td className="border px-2 py-1">{meaning}</td>
                <td className="border px-2 py-1 font-mono text-primary">{ex}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-500">
          Parentheses <code className="bg-gray-100 px-1 rounded font-mono">()</code> can be used to control precedence.
        </p>
      </div>
    ),
  },
  {
    title: "Math Functions",
    content: (
      <div className="space-y-2 text-sm text-gray-700">
        <p>
          The full <code className="bg-gray-100 px-1 rounded font-mono">Math</code> object is available. Common functions:
        </p>
        <table className="w-full text-xs border-collapse mt-1">
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-2 py-1 text-left font-medium">Function</th>
              <th className="border px-2 py-1 text-left font-medium">Description</th>
              <th className="border px-2 py-1 text-left font-medium">Example</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["Math.round(x)", "Round to nearest integer", "Math.round(${Price})"],
              ["Math.floor(x)", "Round down", "Math.floor(${Score} / 10)"],
              ["Math.ceil(x)", "Round up", "Math.ceil(${Hours})"],
              ["Math.abs(x)", "Absolute value", "Math.abs(${Delta})"],
              ["Math.max(a, b)", "Maximum of two values", "Math.max(${A}, ${B})"],
              ["Math.min(a, b)", "Minimum of two values", "Math.min(${A}, ${B})"],
              ["Math.sqrt(x)", "Square root", "Math.sqrt(${Area})"],
              ["Math.pow(x, y)", "Power", "Math.pow(${Base}, 2)"],
            ].map(([fn, desc, ex]) => (
              <tr key={fn as string}>
                <td className="border px-2 py-1 font-mono text-xs">{fn}</td>
                <td className="border px-2 py-1">{desc}</td>
                <td className="border px-2 py-1 font-mono text-xs text-primary">{ex}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    title: "String Operations",
    content: (
      <div className="space-y-2 text-sm text-gray-700">
        <p>When the result type is <strong>Text</strong>, fields are concatenated as strings.</p>
        <div className="space-y-2 mt-2">
          {[
            { formula: "${First Name} ${Last Name}", desc: "Concatenate with a space" },
            { formula: "${City}, ${Country}", desc: "Add literal text between fields" },
            { formula: "Ref-${ID}", desc: "Prefix with static text" },
            { formula: "${Status} — ${Priority}", desc: "Join multiple fields" },
          ].map(({ formula, desc }) => (
            <div key={formula} className="flex items-start gap-2">
              <code className="bg-gray-50 px-2 py-1 rounded font-mono text-xs flex-shrink-0 text-primary">{formula}</code>
              <span className="text-xs text-gray-500 self-center">{desc}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Use <code className="bg-gray-100 px-1 rounded font-mono">Number({"${field}"})</code> to explicitly cast a field value to a number in mixed expressions.
        </p>
      </div>
    ),
  },
  {
    title: "Conditional Logic",
    content: (
      <div className="space-y-2 text-sm text-gray-700">
        <p>JavaScript ternary expressions work in formulas:</p>
        <div className="space-y-2 mt-2">
          {[
            { formula: "${Score} >= 80 ? 'Pass' : 'Fail'", desc: "Pass/fail threshold" },
            { formula: "${Quantity} > 0 ? ${Price} * ${Quantity} : 0", desc: "Guard against empty fields" },
            { formula: "${Discount} ? ${Price} * (1 - ${Discount}/100) : ${Price}", desc: "Optional discount" },
          ].map(({ formula, desc }) => (
            <div key={formula} className="flex flex-col gap-0.5">
              <code className="bg-gray-50 px-2 py-1 rounded font-mono text-xs text-primary">{formula}</code>
              <span className="text-xs text-gray-500 pl-1">{desc}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Date Functions",
    content: (
      <div className="space-y-3 text-sm text-gray-700">
        <p>These built-in functions work with date field values (ISO strings) and can be combined freely with arithmetic operators.</p>
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-2 py-1 text-left font-medium">Function</th>
              <th className="border px-2 py-1 text-left font-medium">Returns</th>
              <th className="border px-2 py-1 text-left font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["today()", "ISO string", "Today's date at midnight (UTC)"],
              ["now()", "ISO string", "Current date and time"],
              ["dateDiff(a, b, unit?)", "Number", "Difference from date a to date b. unit: 'days' (default), 'hours', 'minutes', 'weeks', 'months', 'years'"],
              ["dateAdd(date, n, unit?)", "ISO string", "Add n units to a date. unit: same as dateDiff. Use negative n to subtract."],
              ["dateFormat(date, fmt?)", "Text", "Format a date. fmt: 'date' (YYYY-MM-DD, default), 'datetime', 'relative' (e.g. 'In 3 days')"],
              ["new Date(value)", "Date object", "Parse any date string or timestamp into a Date object usable with .getFullYear(), .getMonth(), etc."],
            ].map(([fn, ret, desc]) => (
              <tr key={fn as string}>
                <td className="border px-2 py-1 font-mono text-xs whitespace-nowrap">{fn}</td>
                <td className="border px-2 py-1 text-xs">{ret}</td>
                <td className="border px-2 py-1">{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="space-y-2 mt-2">
          <p className="font-medium text-xs text-gray-600 uppercase tracking-wide">Quick examples</p>
          {[
            { formula: "dateDiff(${Start Date}, ${End Date})", desc: "Days between two date fields" },
            { formula: "dateDiff(${Start Date}, today())", desc: "Days since a field date (elapsed)" },
            { formula: "dateDiff(today(), ${Deadline})", desc: "Days remaining until deadline" },
            { formula: "Math.round(dateDiff(${Start Date}, ${End Date}, 'hours'))", desc: "Hours between two dates, rounded" },
            { formula: "dateAdd(${Close Date}, 30)", desc: "Date 30 days after a field" },
            { formula: "dateAdd(today(), -7)", desc: "ISO string for 7 days ago" },
            { formula: "dateFormat(${Deadline}, 'relative')", desc: "Human-readable: 'In 5 days' or '2 days ago'" },
            { formula: "dateFormat(dateAdd(${Start Date}, 90), 'date')", desc: "Format a computed date as YYYY-MM-DD" },
            { formula: "dateDiff(today(), ${Deadline}) < 0 ? 'Overdue' : 'On track'", desc: "Conditional status from a deadline" },
          ].map(({ formula, desc }) => (
            <div key={formula} className="flex flex-col gap-0.5">
              <code className="bg-gray-50 px-2 py-1 rounded font-mono text-xs text-primary">{formula}</code>
              <span className="text-xs text-gray-500 pl-1">{desc}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    title: "Stage & Board Aggregation Variables",
    content: (
      <div className="space-y-2 text-sm text-gray-700">
        <p>In <strong>Stage Computed Fields</strong> and <strong>Board Computed Fields</strong>, special aggregate variables are available instead of individual item values:</p>
        <table className="w-full text-xs border-collapse mt-1">
          <thead>
            <tr className="bg-gray-50">
              <th className="border px-2 py-1 text-left font-medium">Variable</th>
              <th className="border px-2 py-1 text-left font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["${cardCount}", "Number of cards in the stage / board"],
              ["${count}", "Alias for cardCount"],
              ["${FieldName}", "Sum of that field across all cards"],
              ["${sum_FieldName}", "Explicit sum"],
              ["${avg_FieldName}", "Average of the field"],
              ["${count_FieldName}", "Number of cards with a value for that field"],
            ].map(([v, d]) => (
              <tr key={v as string}>
                <td className="border px-2 py-1 font-mono">{v}</td>
                <td className="border px-2 py-1">{d}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ),
  },
  {
    title: "Examples",
    content: (
      <div className="space-y-3 text-sm">
        {[
          {
            label: "Revenue (Number)",
            formula: "${Price} * ${Quantity}",
            desc: "Multiply two numeric fields.",
          },
          {
            label: "Discounted Price (Number)",
            formula: "${Price} * (1 - ${Discount} / 100)",
            desc: "Apply a percentage discount.",
          },
          {
            label: "Margin % (Number)",
            formula: "Math.round((${Revenue} - ${Cost}) / ${Revenue} * 100)",
            desc: "Calculate margin as a rounded percentage.",
          },
          {
            label: "Days Remaining (Number)",
            formula: "Math.round(dateDiff(today(), ${Deadline}))",
            desc: "Days until the Deadline field, negative when overdue.",
          },
          {
            label: "Duration in Days (Number)",
            formula: "Math.round(dateDiff(${Start Date}, ${End Date}))",
            desc: "Number of days between two date fields.",
          },
          {
            label: "Deadline Status (Text)",
            formula: "dateDiff(today(), ${Deadline}) < 0 ? 'Overdue' : dateFormat(${Deadline}, 'relative')",
            desc: "Shows 'Overdue' if past, or a friendly countdown otherwise.",
          },
          {
            label: "Full Name (Text)",
            formula: "${First Name} ${Last Name}",
            desc: "Concatenate two text fields with a space.",
          },
          {
            label: "Status Label (Text)",
            formula: "${Score} >= 70 ? 'Qualified' : 'Nurturing'",
            desc: "Derive a text label from a score.",
          },
          {
            label: "Stage Total Revenue (Stage field)",
            formula: "${Revenue}",
            desc: "Sum of the Revenue field across all cards in the stage.",
          },
          {
            label: "Average Deal (Stage field)",
            formula: "${cardCount} > 0 ? ${Revenue} / ${cardCount} : 0",
            desc: "Average revenue per card, guarded against empty stage.",
          },
        ].map(({ label, formula, desc }) => (
          <div key={label} className="border rounded p-3 space-y-1">
            <div className="font-medium text-gray-800 text-xs uppercase tracking-wide">{label}</div>
            <code className="block bg-gray-50 px-2 py-1.5 rounded font-mono text-xs text-primary">{formula}</code>
            <p className="text-xs text-gray-500">{desc}</p>
          </div>
        ))}
      </div>
    ),
  },
  {
    title: "Tips & Limitations",
    content: (
      <ul className="text-sm text-gray-700 space-y-1.5 list-disc list-inside">
        <li>Field names are <strong>case-insensitive</strong> — <code className="bg-gray-100 px-1 rounded font-mono">{`\${price}`}</code> and <code className="bg-gray-100 px-1 rounded font-mono">{`\${Price}`}</code> are the same.</li>
        <li>If a referenced field has no value on a card, it is treated as <code className="bg-gray-100 px-1 rounded font-mono">null</code> (0 in numeric context).</li>
        <li>Formulas run in a sandboxed environment — no network access or file I/O.</li>
        <li>Execution is capped at <strong>100 ms</strong> — keep formulas simple.</li>
        <li>Computed fields can reference other computed fields, as long as there are no circular dependencies.</li>
        <li>For Number result type, the formula must evaluate to a numeric value or the field will show an error.</li>
      </ul>
    ),
  },
];

export default function FormulaHelpModal({ onClose }: FormulaHelpModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Formula Reference</h2>
            <p className="text-sm text-gray-500 mt-0.5">Everything you can do with computed field formulas</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
          {SECTIONS.map(({ title, content }) => (
            <section key={title}>
              <h3 className="text-sm font-semibold text-gray-900 mb-2 pb-1 border-b">{title}</h3>
              {content}
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t text-right">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium text-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
